import { Download, FlightTakeoff, Folder, MoreVert, Visibility, VisibilityOutlined } from "@mui/icons-material";
import { CircularProgress, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, useTheme } from "@mui/material";
import type { HierarcicalObjectReference, ObjectData } from "@novorender/data-js-api";
import { t } from "i18next";
import { useCallback, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";

import { useLazyGetFileDownloadLinkQuery } from "apis/dataV2/dataV2Api";
import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { ObjectTreeCheckbox } from "components/objectTree/objectTreeCheckbox";
import { ObjectTreeGroupExpander } from "components/objectTree/objectTreeGroupExpander";
import { ObjectTreeListItemButton } from "components/objectTree/objectTreeListItemButton";
import { ObjectTreeNodeExpander } from "components/objectTree/objectTreeNodeExpander";
import { ObjectTreeNodeLabel } from "components/objectTree/objectTreeNodeLabel";
import type { ListNode } from "components/objectTree/types";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { hiddenActions, useDispatchHidden, useHidden } from "contexts/hidden";
import { highlightActions, useDispatchHighlighted, useHighlighted } from "contexts/highlighted";
import { selectionBasketActions, useDispatchSelectionBasket, useSelectionBasket } from "contexts/selectionBasket";
import { shownActions, useDispatchShown, useShown } from "contexts/shown";
import { getImageFromObject } from "features/images";
import { renderActions, selectDefaultVisibility, selectMainObject } from "features/render";
import { isGlSpace } from "features/render/utils";
import useFlyTo from "hooks/useFlyTo";
import { selectIsProcoreDesign, selectTmZoneForCalc } from "slices/explorer";
import { telemetry } from "telemetry";
import type { AsyncState } from "types/misc";
import { AsyncStatus } from "types/misc";
import { hasMouseSupport } from "utils/misc";
import { selectObjectHierarchy, unselectObjectHierarchy } from "utils/objectActions";
import { getTotalBoundingSphere, isLeafNode } from "utils/objectData";
import type { IdList } from "utils/selectionSet";
import type { GroupState } from "utils/treeHelpers";
import { getGroupStateForIdSet } from "utils/treeHelpers";

import type { ModelTreeNodeData, TreeNodeRendererProps } from "../types";
import { getModelTreeVisibilityMode, getNodeVisibilityState, isObjectVisible } from "../visibility";

const DOWNLOAD_PROPERTY = "Novorender/Download";

export function ModelTreeNode({
    item,
    setLoading,
    abortSignal,
    onToggleExpanded,
    onExpandGroup,
    highlightPath,
    tree,
}: TreeNodeRendererProps) {
    const {
        state: { dataAccess },
    } = useExplorerGlobals(true);
    const theme = useTheme();
    const highlightedSet = useHighlighted();
    const hiddenSet = useHidden();
    const shownSet = useShown();
    const basket = useSelectionBasket();
    const mainObject = useAppSelector(selectMainObject);
    const defaultVisibility = useAppSelector(selectDefaultVisibility);
    const isProcoreDesign = useAppSelector(selectIsProcoreDesign);
    const mode = getModelTreeVisibilityMode(defaultVisibility, isProcoreDesign);
    const dispatchHighlighted = useDispatchHighlighted();
    const dispatchHidden = useDispatchHidden();
    const dispatchShown = useDispatchShown();
    const dispatchSelectionBasket = useDispatchSelectionBasket();
    const dispatch = useAppDispatch();
    const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

    const [metadata, setMetadata] = useState<AsyncState<ObjectData>>({ status: AsyncStatus.Initial });

    const selected: GroupState = useMemo(
        () => getGroupStateForIdSet(highlightedSet.ids, item, tree),
        [item, highlightedSet, tree],
    );

    const visible: GroupState = useMemo(
        () =>
            getNodeVisibilityState({
                mode,
                hidden: hiddenSet.ids,
                shown: shownSet.ids,
                basket: basket.ids,
                item,
                tree,
            }),
        [mode, item, tree, hiddenSet, shownSet, basket],
    );

    const ownId = item.node.data.object.id;
    const isRowSelected = item.nodeGroup ? selected === "all" : highlightedSet.ids.has(ownId);
    const isRowVisible = item.nodeGroup
        ? visible === "all"
        : isObjectVisible({ mode, hidden: hiddenSet.ids, shown: shownSet.ids, basket: basket.ids, id: ownId });

    const highlight = useMemo(
        () =>
            highlightPath
                ? ((item.node.path === highlightPath ||
                      item.nodes?.some((n) => n.path === highlightPath) ||
                      item.nodeGroup?.some((n) => n.path === highlightPath)) ??
                  false)
                : false,
        [highlightPath, item],
    );

    const selectObject = async (
        nodes: HierarcicalObjectReference[],
        abortSignal: AbortSignal,
        setLoading: (value: boolean) => void,
    ) => {
        // Set main object for single leaf node selection (widget-specific feature)
        if (isLeafNode(nodes[0]) && nodes.length === 1) {
            dispatch(renderActions.setMainObject(nodes[0].id));
        }

        await selectObjectHierarchy({
            nodes,
            dataAccess,
            abortSignal,
            setLoading,
            dispatchHighlighted,
            dispatchHidden,
        });
    };

    const unselectObject = async (
        nodes: HierarcicalObjectReference[],
        abortSignal: AbortSignal,
        setLoading: (value: boolean) => void,
    ) => {
        await unselectObjectHierarchy({ nodes, dataAccess, abortSignal, setLoading, dispatchHighlighted });
    };

    const applyVisibility = async (nodes: HierarcicalObjectReference[], makeVisible: boolean) => {
        const dropFromSelection = (ids: IdList) => {
            dispatchSelectionBasket(selectionBasketActions.remove(ids));
            dispatchHighlighted(highlightActions.remove(ids));
            if (mainObject !== undefined && ids.includes(mainObject)) {
                dispatch(renderActions.setMainObject(undefined));
            }
        };

        const applyToIds = (ids: IdList) => {
            if (ids.length === 0) {
                return;
            }

            if (makeVisible) {
                dispatchHidden(hiddenActions.remove(ids));
                if (mode === "additive") {
                    dispatchShown(shownActions.add(ids));
                }
            } else {
                if (mode === "subtractive") {
                    dispatchHidden(hiddenActions.add(ids));
                }
                dispatchShown(shownActions.remove(ids));
                dropFromSelection(ids);
            }
        };

        if (isLeafNode(nodes[0])) {
            applyToIds(nodes.map((n) => n.id));
            return;
        }

        setLoading(true);

        const node = nodes[0];
        try {
            const descendantIds = await dataAccess.getDescendantIds(node, abortSignal);

            if (!abortSignal.aborted) {
                applyToIds(descendantIds);
                applyToIds([node.id]);
            }
        } catch (e) {
            if (!abortSignal.aborted) {
                telemetry.trackCaughtException({
                    source: "modelTree.modelTreeNode.applyVisibility",
                    category: "data",
                    error: e,
                });
            }
        } finally {
            setLoading(false);
        }
    };

    const stopPropagation: React.MouseEventHandler = (e) => {
        e.stopPropagation();
    };

    const openMenu = async (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        setMenuAnchor(e.currentTarget);

        if (!item.node.groupCollapsed) {
            setMetadata({ status: AsyncStatus.Loading });
            try {
                setMetadata({ status: AsyncStatus.Success, data: await item.node.data.object.loadMetaData() });
            } catch (ex) {
                console.warn(ex);
                telemetry.trackCaughtException({
                    source: "modelTree.modelTreeNode.openMenu",
                    category: "data",
                    error: ex,
                });
                setMetadata({ status: AsyncStatus.Initial });
            }
        }
    };

    const closeMenu = (e?: unknown) => {
        (e as React.MouseEvent<HTMLButtonElement>)?.stopPropagation();
        setMenuAnchor(null);
    };

    const handleClick = () => {
        if (!item.node.isLeaf) {
            onToggleExpanded(item);
            dispatch(renderActions.setMainObject(item.node.data.object.id));
        } else {
            handleSelection();
        }
    };

    const handleToggleVisibility = () => {
        const objects = item.nodeGroup?.map((n) => n.data.object) ?? [item.node.data.object];
        void applyVisibility(objects, !isRowVisible);
    };

    const handleSelection = () => {
        const objects = item.nodeGroup?.map((n) => n.data.object) ?? [item.node.data.object];
        if (selected === "all" || isRowSelected) {
            void unselectObject(objects, abortSignal, setLoading);
        } else {
            void selectObject(objects, abortSignal, setLoading);
        }
    };

    const currentNode = item.nodes?.at(-1) ?? item.node;

    const showExpander = !currentNode.isLeaf;

    return (
        <ObjectTreeListItemButton
            item={item}
            onClick={handleClick}
            selected={highlight}
            sx={
                menuAnchor
                    ? undefined
                    : {
                          "&:not(:hover) .show-on-hover": { display: "none" },
                      }
            }
        >
            {showExpander && <ObjectTreeNodeExpander item={item} onToggle={onToggleExpanded} />}
            <ObjectTreeNodeLabel
                flex="auto"
                item={item}
                sx={{ pl: showExpander ? undefined : theme.customSpacing.objectTreeExpanderPadding }}
                highlightPath={highlightPath}
                onNodeClick={(node) => {
                    dispatch(renderActions.setMainObject(node.data.object.id));
                }}
            />
            {hasMouseSupport() && (
                <IconButton onClick={openMenu} sx={{ p: 0.25 }} className="show-on-hover">
                    {metadata.status === AsyncStatus.Loading ? <CircularProgress size={24} /> : <MoreVert />}
                </IconButton>
            )}
            {item.nodeGroup && onExpandGroup && <ObjectTreeGroupExpander item={item} onClick={onExpandGroup} />}
            <ObjectTreeCheckbox
                name="toggle node highlight"
                aria-label="toggle node highlight"
                size="small"
                checked={selected === "all"}
                indeterminate={selected === "some"}
                onChange={handleSelection}
                onClick={stopPropagation}
            />
            <ObjectTreeCheckbox
                name="toggle node visibility"
                aria-label="Toggle node visibility"
                size="small"
                icon={<Visibility color="disabled" />}
                checkedIcon={<Visibility color={mode === "additive" ? "primary" : "secondary"} />}
                checked={visible === "all"}
                indeterminate={visible === "some"}
                indeterminateIcon={<VisibilityOutlined color="action" />}
                onChange={handleToggleVisibility}
                onClick={stopPropagation}
            />
            {!hasMouseSupport() && (
                <IconButton onClick={openMenu} sx={{ p: 0.25 }}>
                    {metadata.status === AsyncStatus.Loading ? <CircularProgress size={24} /> : <MoreVert />}
                </IconButton>
            )}

            <NodeMenu item={item} metadata={metadata} menuAnchor={menuAnchor} closeMenu={closeMenu} />
        </ObjectTreeListItemButton>
    );
}

function NodeMenu({
    item,
    metadata,
    menuAnchor,
    closeMenu,
}: {
    item: ListNode<ModelTreeNodeData>;
    metadata: AsyncState<ObjectData>;
    menuAnchor: HTMLElement | null;
    closeMenu: () => void;
}) {
    const {
        state: { scene },
    } = useExplorerGlobals(true);
    const tmZone = useAppSelector(selectTmZoneForCalc);
    const history = useHistory();
    const flyTo = useFlyTo();
    const [getFileDownloadLink] = useLazyGetFileDownloadLinkQuery();

    const getImage = useCallback(
        (objectData: ObjectData) => getImageFromObject({ objectData, flip: isGlSpace(scene.up), tmZone }),
        [scene.up, tmZone],
    );

    const handleFlyTo = (e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        closeMenu();

        const image = metadata.status === AsyncStatus.Success && getImage(metadata.data);

        if (image) {
            void flyTo({ sphere: { center: image.position, radius: 20 } });
            return;
        }

        const sphere = item.nodeGroup
            ? getTotalBoundingSphere(item.nodeGroup.map((n) => n.data.object))
            : item.node.data.object.bounds?.sphere;
        if (sphere) {
            void flyTo({ sphere });
        }
    };

    const handleSaveToGroups = (e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        closeMenu();
        history.push("/saveToGroups", { node: item.node });
    };

    const handleDownloadOrOpen = async () => {
        closeMenu();

        if (metadata.status !== AsyncStatus.Success) {
            return;
        }

        const relativeUrl = metadata.data.properties.find((p) => p[0] === DOWNLOAD_PROPERTY)![1];
        const downloadLink = await getFileDownloadLink({ relativeUrl }).unwrap();

        const link = document.createElement("a");
        link.href = downloadLink;
        document.body.appendChild(link);
        link.click();
        link.remove();
    };

    const canFlyTo = useMemo(() => {
        if (item.nodeGroup ? item.nodeGroup.some((n) => n.data.object.bounds) : item.node.data.object.bounds) {
            return true;
        }

        // Images don't have bounds, but they have (sometimes) coordinates stored under "Image" property group
        return metadata.status === AsyncStatus.Success && Boolean(getImage(metadata.data));
    }, [item, metadata, getImage]);

    return (
        <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={closeMenu}
            anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
            }}
            transformOrigin={{
                vertical: "top",
                horizontal: "right",
            }}
        >
            <MenuItem onClick={handleFlyTo} disabled={!canFlyTo}>
                <ListItemIcon>
                    <FlightTakeoff />
                </ListItemIcon>
                <ListItemText>{t("flyTo")}</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleSaveToGroups}>
                <ListItemIcon>
                    <Folder />
                </ListItemIcon>
                <ListItemText>{t("saveToGroups")}</ListItemText>
            </MenuItem>
            {metadata.status === AsyncStatus.Success &&
                metadata.data.properties.some((p) => p[0] === DOWNLOAD_PROPERTY) && (
                    <MenuItem onClick={handleDownloadOrOpen}>
                        <ListItemIcon>
                            <Download />
                        </ListItemIcon>
                        <ListItemText>{t("download")}</ListItemText>
                    </MenuItem>
                )}
        </Menu>
    );
}
